import React, { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Loader2, AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  type CarouselApi,
} from "@/components/ui/carousel";

// API-tól várt komment struktúra
interface ApiComment {
  id: number;
  user_name: string;
  avatar_url?: string;
  content: string;
  created_at: string;
  game_id: number;
  game_title?: string;
  game_slug?: string;
}

const fetchRecentComments = async (): Promise<ApiComment[]> => {
  try {
    // API hívás az utolsó kommentekért
    const res = await fetch(`/api/comments/last`);
    if (!res.ok) throw new Error('A hozzászólások lekérdezése sikertelen.');
    const apiData: ApiComment[] = await res.json();

    return apiData.map(comment => ({
      ...comment,
      // fallback mezők, ha valami hiányzik az API-ból
      game_title: comment.game_title || 'Ismeretlen Játék'
    }));
  } catch (err) {
    console.error(err);
    throw err;
  }
};

const RecentCommentsPanel: React.FC<{ maxItemsPerView?: number }> = ({ maxItemsPerView = 3 }) => {
  const { data: comments, isLoading, isError } = useQuery({
    queryKey: ['recentCommentsPanel'],
    queryFn: fetchRecentComments,
  });
  const [api, setApi] = useState<CarouselApi>();

  // Dinamikus basis osztály a maxItemsPerView alapján
  const getBasisClass = () => {
    switch (maxItemsPerView) {
      case 1:
        return 'md:basis-full lg:basis-full';
      case 2:
        return 'md:basis-1/2 lg:basis-1/2';
      default:
        return 'md:basis-1/2 lg:basis-1/3';
    }
  };


  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full min-h-48 bg-card rounded-lg">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center h-full min-h-48 bg-card rounded-lg text-destructive">
        <AlertTriangle className="w-8 h-8 mr-2" /> Hiba
      </div>
    );
  }

  return (
    <div className="text-card-foreground border-border shadow-md h-full p-0 min-h-40 max-h-40 overflow-y-auto flex flex-col items-center justify-center">
      <Carousel
        setApi={setApi}
        opts={{
          align: "start",
          loop: true,
        }}
        className="w-full h-full relative"
      >
        <CarouselContent className="-ml-4">
          {(comments || []).map((comment) => (
            <CarouselItem key={comment.id} className={`pl-4 ${getBasisClass()}`}>
              <div className="p-1 h-full">
                <Link
                  to={comment.game_slug ? `/games/${comment.game_slug}?commentId=${comment.id}` : '#'}
                  className="block p-4 rounded-lg bg-secondary hover:bg-muted transition-colors h-full flex flex-col justify-between"
                >
                  <p
                    className="text-sm line-clamp-3 italic"
                    dangerouslySetInnerHTML={{
                      __html: `"${(comment.content || '').replace(/<[^>]+>/g, '')}"`,
                    }}
                  />
                  <div className="mt-3 text-xs text-right">
                    <p className="font-semibold text-foreground">
                      {comment.user_name} a(z){' '}
                      <span className="font-semibold text-primary">
                        {comment.game_title}
                      </span>{' '}
                      játékhoz
                    </p>
                    <p className="text-muted-foreground mt-1">
                      {new Date(comment.created_at).toLocaleString('hu-HU')}
                    </p>
                  </div>
                </Link>
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        <div className="absolute top-1/2 -translate-y-1/2 flex justify-between w-full z-10 px-2 pointer-events-none">
          <button
            onClick={() => api?.scrollPrev()}
            className="p-2 rounded-full bg-background/50 text-foreground hover:bg-background/80 transition-colors pointer-events-auto"
            aria-label="Előző"
          >
            <ChevronLeft size={20} />
          </button>
          <button
            onClick={() => api?.scrollNext()}
            className="p-2 rounded-full bg-background/50 text-foreground hover:bg-background/80 transition-colors pointer-events-auto"
            aria-label="Következő"
          >
            <ChevronRight size={20} />
          </button>
        </div>
      </Carousel>
    </div>
  );
};

export default RecentCommentsPanel;
